import {
  ActionIcon,
  Card,
  Group,
  Stack,
  Text,
  TextInput,
  Select,
  Table,
  Badge,
  Loader,
  Center,
  Title,
  Pagination,
  Tooltip,
  Image,
  Drawer,
  SimpleGrid,
} from '@mantine/core';
import { useDebouncedValue, useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { IconAlertTriangle, IconPhoto, IconSearch, IconWorldSearch } from '@tabler/icons-react';
import { api } from '../lib/api';
import { num } from '../lib/format';

type SpecsSourceType = 'oem_api' | 'dealer' | 'grounded' | 'none';

interface Part {
  part_number: string;
  manufacturer: string | null;
  product_type: string | null;
  category: string | null;
  weight_lbs: number | null;
  dimensions: {
    length_in: number | null;
    width_in: number | null;
    height_in: number | null;
  } | null;
  applicable_subsystems: string[] | null;
  description: string | null;
  specs_source: string | null;
  specs_source_type: SpecsSourceType;
  n_clients_stocking: number | null;
  primary_image_sha256: string | null;
}

interface PartImage {
  sha256: string;
  source_client_num: number;
  source_account_prefix: string | null;
  mime_type: string;
  byte_size: number;
  captured_at: string;
  is_useful: boolean | null;
  image_type: string | null;
  visible_part_number: string | null;
  visible_brand: string | null;
  has_watermark: boolean | null;
  watermark_type: string | null;
  watermark_text: string | null;
  confidence: string | null;
  notes: string | null;
}

const IMAGE_TYPE_COLOR: Record<string, string> = {
  photo: 'teal',
  diagram: 'indigo',
  label: 'orange',
  packaging: 'lime',
  screenshot: 'gray',
  logo: 'gray',
  other: 'dark',
};

const SOURCE_BADGE: Record<SpecsSourceType, { label: string; color: string } | null> = {
  oem_api: { label: 'OEM', color: 'teal' },
  dealer: { label: 'Dealer', color: 'blue' },
  grounded: { label: 'Grounded', color: 'yellow' },
  none: null,
};

interface ListResp {
  data: Part[];
  total: number;
  page: number;
  page_size: number;
}

const PAGE_SIZE = 50;

export default function PartsPage() {
  const [search, setSearch] = useState('');
  const [debounced] = useDebouncedValue(search, 300);
  const [manufacturer, setManufacturer] = useState<string | null>(null);
  const [specsOnly, setSpecsOnly] = useState<string | null>(null);
  const [stocked, setStocked] = useState<string | null>(null);
  const [hasImages, setHasImages] = useState<string | null>(null);
  const [client, setClient] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [imagesForPart, setImagesForPart] = useState<string | null>(null);
  const [drawerOpened, { open: openDrawer, close: closeDrawer }] = useDisclosure(false);
  const queryClient = useQueryClient();

  const [pendingPart, setPendingPart] = useState<string | null>(null);

  const groundMutation = useMutation({
    mutationFn: (partNumber: string) =>
      api<{ updated: boolean; note: string | null; source_domain: string | null }>(
        `/api/parts/${encodeURIComponent(partNumber)}/ground-query`,
        { method: 'POST' },
      ),
    onSuccess: (data, partNumber) => {
      if (data.updated) {
        notifications.show({
          color: 'teal',
          autoClose: 5000,
          withBorder: true,
          title: `Updated ${partNumber}`,
          message: data.source_domain
            ? `Source: ${data.source_domain}`
            : (data.note ?? 'Specs written.'),
          styles: { root: { minWidth: 320 } },
        });
      } else {
        notifications.show({
          color: 'gray',
          autoClose: 5000,
          withBorder: true,
          title: `No data for ${partNumber}`,
          message: data.note ?? 'Grounded search returned no usable specs.',
          styles: { root: { minWidth: 320 } },
        });
      }
      queryClient.invalidateQueries({ queryKey: ['parts'] });
    },
    onError: (err: Error, partNumber) => {
      notifications.show({
        color: 'red',
        autoClose: 8000,
        withBorder: true,
        title: `Ground query failed for ${partNumber}`,
        message: err.message,
        styles: { root: { minWidth: 320 } },
      });
    },
    onSettled: () => setPendingPart(null),
  });

  const runGround = (partNumber: string) => {
    setPendingPart(partNumber);
    groundMutation.mutate(partNumber);
  };

  const mfgs = useQuery({
    queryKey: ['metrics', 'manufacturers'],
    queryFn: () => api<{ manufacturer: string; total: number }[]>('/api/metrics/manufacturers'),
  });

  const clients = useQuery({
    queryKey: ['metrics', 'clients'],
    queryFn: () =>
      api<{ client_num: number; stocked_parts: number; hostname: string | null }[]>(
        '/api/metrics/clients',
      ),
  });

  const query = useQuery({
    queryKey: ['parts', { debounced, manufacturer, specsOnly, stocked, hasImages, client, page }],
    queryFn: () => {
      const params = new URLSearchParams({
        page: String(page),
        page_size: String(PAGE_SIZE),
      });
      if (debounced) {
        params.set('q', debounced);
      }
      if (manufacturer) {
        params.set('manufacturer', manufacturer);
      }
      if (specsOnly) {
        params.set('filter', specsOnly);
      }
      if (stocked) {
        params.set('stocked', stocked);
      }
      if (hasImages) {
        params.set('has_images', hasImages);
      }
      if (client) {
        params.set('client', client);
      }
      return api<ListResp>(`/api/parts?${params.toString()}`);
    },
  });

  const partImagesQuery = useQuery({
    queryKey: ['parts', 'images', imagesForPart],
    queryFn: () =>
      api<{ part_number: string; images: PartImage[] }>(
        `/api/parts/${encodeURIComponent(imagesForPart!)}/images`,
      ),
    enabled: imagesForPart !== null,
  });

  const openImages = (partNumber: string) => {
    setImagesForPart(partNumber);
    openDrawer();
  };

  const totalPages = query.data ? Math.ceil(query.data.total / PAGE_SIZE) : 0;

  return (
    <Stack gap="md">
      <Title order={2}>Parts</Title>

      <Card withBorder padding="md">
        <Group wrap="wrap" gap="sm">
          <TextInput
            placeholder="Search part number, description, product type"
            leftSection={<IconSearch size={16} />}
            value={search}
            onChange={(e) => {
              setSearch(e.currentTarget.value);
              setPage(1);
            }}
            style={{ flex: 1, minWidth: 260 }}
          />
          <Select
            placeholder="Manufacturer"
            data={(mfgs.data ?? []).slice(0, 50).map((m) => ({
              value: m.manufacturer,
              label: `${m.manufacturer} (${num(m.total)})`,
            }))}
            value={manufacturer}
            onChange={(v) => {
              setManufacturer(v);
              setPage(1);
            }}
            clearable
            searchable
            style={{ minWidth: 220 }}
          />
          <Select
            placeholder="Specs"
            data={[
              { value: 'with_weight', label: 'With weight' },
              { value: 'no_weight', label: 'Missing weight' },
              { value: 'full_dims', label: 'With full dims' },
            ]}
            value={specsOnly}
            onChange={(v) => {
              setSpecsOnly(v);
              setPage(1);
            }}
            clearable
          />
          <Select
            placeholder="Inventory"
            data={[
              { value: 'true', label: 'Stocked (in PIV1)' },
              { value: 'false', label: 'Not stocked' },
            ]}
            value={stocked}
            onChange={(v) => {
              setStocked(v);
              setPage(1);
            }}
            clearable
          />
          <Select
            placeholder="Images"
            data={[
              { value: 'true', label: 'Has image' },
              { value: 'false', label: 'No image' },
            ]}
            value={hasImages}
            onChange={(v) => {
              setHasImages(v);
              setPage(1);
            }}
            clearable
          />
          <Select
            placeholder="Client"
            data={(clients.data ?? []).map((cl) => ({
              value: String(cl.client_num),
              label: `${cl.hostname ?? `Client ${cl.client_num}`} (${num(cl.stocked_parts)})`,
            }))}
            value={client}
            onChange={(v) => {
              setClient(v);
              setPage(1);
            }}
            clearable
            searchable
            style={{ minWidth: 240 }}
          />
        </Group>
      </Card>

      <Card withBorder padding={0}>
        {query.isLoading ? (
          <Center p="xl">
            <Loader />
          </Center>
        ) : query.data?.data.length === 0 ? (
          <Center p="xl">
            <Text c="dimmed">No results</Text>
          </Center>
        ) : (
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th style={{ width: 56 }}>Image</Table.Th>
                <Table.Th>Part Number</Table.Th>
                <Table.Th>Manufacturer</Table.Th>
                <Table.Th>Product Type</Table.Th>
                <Table.Th>Category</Table.Th>
                <Table.Th style={{ textAlign: 'right' }}>Weight (lb)</Table.Th>
                <Table.Th>L×W×H (in)</Table.Th>
                <Table.Th>Subsystems</Table.Th>
                <Table.Th>Source</Table.Th>
                <Table.Th style={{ textAlign: 'right' }}>Stocked by</Table.Th>
                <Table.Th style={{ width: 56, textAlign: 'center' }}>Actions</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {query.data?.data.map((p) => {
                const badge = SOURCE_BADGE[p.specs_source_type];
                return (
                  <Table.Tr key={p.part_number}>
                    <Table.Td>
                      {p.primary_image_sha256 ? (
                        <Tooltip label="View all images">
                          <ActionIcon
                            variant="subtle"
                            size={44}
                            radius="sm"
                            onClick={() => openImages(p.part_number)}
                            aria-label={`Images for ${p.part_number}`}
                          >
                            <Image
                              src={`/api/images/${p.primary_image_sha256}/bytes`}
                              alt={p.part_number}
                              w={40}
                              h={40}
                              fit="contain"
                              radius="sm"
                            />
                          </ActionIcon>
                        </Tooltip>
                      ) : (
                        <Text c="dimmed" ta="center">
                          —
                        </Text>
                      )}
                    </Table.Td>
                    <Table.Td>
                      <Text ff="monospace" size="sm">
                        {p.part_number}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      {p.manufacturer ?? (
                        <Text c="dimmed" size="sm">
                          —
                        </Text>
                      )}
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{p.product_type ?? '—'}</Text>
                    </Table.Td>
                    <Table.Td>
                      {p.category ? (
                        <Badge variant="light" size="sm">
                          {p.category}
                        </Badge>
                      ) : (
                        '—'
                      )}
                    </Table.Td>
                    <Table.Td style={{ textAlign: 'right' }}>
                      {p.weight_lbs != null ? (
                        p.weight_lbs.toFixed(2)
                      ) : (
                        <Text c="dimmed" size="sm">
                          —
                        </Text>
                      )}
                    </Table.Td>
                    <Table.Td>
                      {p.dimensions?.length_in != null ? (
                        `${p.dimensions.length_in}×${p.dimensions.width_in}×${p.dimensions.height_in}`
                      ) : (
                        <Text c="dimmed" size="sm">
                          —
                        </Text>
                      )}
                    </Table.Td>
                    <Table.Td>
                      {p.applicable_subsystems && p.applicable_subsystems.length > 0 ? (
                        <Group gap={4}>
                          {p.applicable_subsystems.slice(0, 3).map((s) => (
                            <Badge key={s} variant="outline" size="xs">
                              {s}
                            </Badge>
                          ))}
                        </Group>
                      ) : (
                        <Text c="dimmed" size="sm">
                          —
                        </Text>
                      )}
                    </Table.Td>
                    <Table.Td>
                      {badge ? (
                        <Badge
                          variant="light"
                          color={badge.color}
                          size="sm"
                          title={p.specs_source ?? undefined}
                        >
                          {badge.label}
                        </Badge>
                      ) : (
                        <Text c="dimmed" size="sm">
                          —
                        </Text>
                      )}
                    </Table.Td>
                    <Table.Td style={{ textAlign: 'right' }}>
                      {p.n_clients_stocking != null ? (
                        <Badge variant="outline" size="sm" color="orange">
                          {p.n_clients_stocking} {p.n_clients_stocking === 1 ? 'client' : 'clients'}
                        </Badge>
                      ) : (
                        <Text c="dimmed" size="sm">
                          —
                        </Text>
                      )}
                    </Table.Td>
                    <Table.Td style={{ textAlign: 'center' }}>
                      <Tooltip label="Run grounded Gemini query (~$0.036)">
                        <ActionIcon
                          variant="subtle"
                          size="md"
                          color="yellow"
                          loading={pendingPart === p.part_number}
                          disabled={pendingPart !== null && pendingPart !== p.part_number}
                          onClick={() => runGround(p.part_number)}
                          aria-label="Run grounded query"
                        >
                          <IconWorldSearch size={16} />
                        </ActionIcon>
                      </Tooltip>
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        )}
      </Card>

      {totalPages > 1 && (
        <Group justify="space-between">
          <Text c="dimmed" size="sm">
            {num(query.data?.total ?? 0)} matching parts
          </Text>
          <Pagination total={totalPages} value={page} onChange={setPage} />
        </Group>
      )}

      <Drawer
        opened={drawerOpened}
        onClose={() => {
          closeDrawer();
          setTimeout(() => setImagesForPart(null), 200);
        }}
        title={imagesForPart ? `Images for ${imagesForPart}` : 'Images'}
        position="right"
        size="lg"
        padding="md"
      >
        {partImagesQuery.isLoading ? (
          <Center mih={200}>
            <Loader />
          </Center>
        ) : partImagesQuery.data && partImagesQuery.data.images.length > 0 ? (
          <Stack gap="sm">
            <Text size="xs" c="dimmed">
              {partImagesQuery.data.images.length} image
              {partImagesQuery.data.images.length === 1 ? '' : 's'} —{' '}
              {partImagesQuery.data.images.filter((i) => i.is_useful).length} marked useful
            </Text>
            <SimpleGrid cols={2} spacing="sm">
              {partImagesQuery.data.images.map((img) => (
                <Card
                  key={`${img.sha256}-${img.source_client_num}`}
                  withBorder
                  padding="xs"
                  style={img.is_useful === false ? { opacity: 0.55 } : undefined}
                >
                  <Card.Section>
                    <Image
                      src={`/api/images/${img.sha256}/bytes`}
                      alt={imagesForPart ?? ''}
                      h={160}
                      fit="contain"
                    />
                  </Card.Section>
                  <Stack gap={4} mt="xs">
                    <Group gap={4} wrap="wrap">
                      {img.image_type && (
                        <Badge
                          size="xs"
                          color={IMAGE_TYPE_COLOR[img.image_type] ?? 'gray'}
                          variant="light"
                        >
                          {img.image_type}
                        </Badge>
                      )}
                      {img.confidence && img.confidence !== 'high' && (
                        <Badge size="xs" color="yellow" variant="light">
                          {img.confidence}
                        </Badge>
                      )}
                      {img.has_watermark && (
                        <Tooltip
                          label={
                            img.watermark_text
                              ? `${img.watermark_type}: ${img.watermark_text}`
                              : (img.watermark_type ?? 'watermark')
                          }
                        >
                          <Badge
                            size="xs"
                            color={img.watermark_type === 'branded' ? 'red' : 'gray'}
                            variant="light"
                            leftSection={<IconAlertTriangle size={10} />}
                          >
                            wm
                          </Badge>
                        </Tooltip>
                      )}
                      <Badge size="xs" color="gray" variant="outline">
                        client {img.source_client_num}
                      </Badge>
                    </Group>
                    {(img.visible_part_number || img.visible_brand) && (
                      <Text size="xs" c="dimmed" truncate>
                        {[img.visible_brand, img.visible_part_number].filter(Boolean).join(' · ')}
                      </Text>
                    )}
                    {img.notes && (
                      <Text size="xs" c="dimmed" lineClamp={2}>
                        {img.notes}
                      </Text>
                    )}
                  </Stack>
                </Card>
              ))}
            </SimpleGrid>
          </Stack>
        ) : (
          <Center mih={200}>
            <Stack align="center" gap="xs">
              <IconPhoto size={36} color="var(--mantine-color-gray-5)" />
              <Text c="dimmed" size="sm">
                No images for this part
              </Text>
            </Stack>
          </Center>
        )}
      </Drawer>
    </Stack>
  );
}
